// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 74605cca-affb-4003-971d-0c2b794af498

// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Primordia — import identity, idempotency and run history
// ---------------------------------------------------------------------------
// Clients re-send inventories constantly: a corrected export, a second vCenter,
// "v3 FINAL". Re-importing must UPDATE the records that already exist, never
// duplicate the estate. That requires answering one question explicitly rather
// than implicitly: what makes two rows the same thing?
//
// Matching on display name alone is not safe. Measured in the loaded MUSC
// estate (7,305 VMs): 58 machines have a DNS short-name equal to a DIFFERENT
// machine's name — "aruba-mm-new" carries DNS "Aruba-MM" while a separate VM is
// actually named "aruba-mm". Key on DNS and those 58 pairs silently merge two
// distinct servers; key on name and a later DNS-keyed file re-creates them all.
//
// So identity is chosen ONCE per source, recorded on every record it writes,
// and reused on every subsequent import from that source.
// ---------------------------------------------------------------------------

// Match key normalisation. Trim and collapse internal whitespace before
// lowercasing: spreadsheet exports routinely carry trailing spaces, and
// "EPIC PRD 01" and "EPIC  PRD 01" are the same host to everyone but a computer.
export const normalizeKey = (v) => String(v == null ? '' : v)
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

// Strip a domain suffix so "srv01.musc.edu" and "srv01" can be compared when —
// and ONLY when — the caller has opted into that behaviour. It is off by
// default precisely because of the 58 collisions above.
export const shortHost = (v) => normalizeKey(v).split('.')[0];

export const IDENTITY_STRATEGIES = [
  {
    key: 'name',
    label: 'Record name',
    help: 'Match on the primary name column. The safe default.',
    get: (row) => normalizeKey(row.name),
  },
  {
    key: 'sourceId',
    label: 'Source system ID',
    help: 'Match on a stable identifier the source system supplies (VM UUID, MoID, asset tag). '
        + 'The most reliable option when the file has one, because it survives a rename.',
    get: (row) => normalizeKey(row.sourceId),
  },
  {
    key: 'dnsName',
    label: 'DNS name',
    help: 'Match on the fully-qualified DNS name. Use only when the source consistently supplies it.',
    get: (row) => normalizeKey(row.dnsName),
  },
  {
    key: 'dnsShort',
    label: 'DNS name (ignore domain)',
    help: 'Match on the DNS name with the domain stripped. RISKY: a short name can collide with a '
        + 'different machine\'s record name — verify the collision report before choosing this.',
    get: (row) => shortHost(row.dnsName),
  },
];

export const strategyFor = (key) =>
  IDENTITY_STRATEGIES.find((s) => s.key === key) || IDENTITY_STRATEGIES[0];

// Where the identity key is stashed on a fact sheet so later imports can match
// on it even after the display name changes.
export const IDENTITY_FIELD = 'importKey';
export const IDENTITY_STRATEGY_FIELD = 'importKeyStrategy';
export const SOURCE_FIELD = 'importSource';

const fieldsOf = (fs) => ({ ...(fs && fs.data), ...(fs && fs.fields) });

// Build a lookup over existing fact sheets. Recorded import keys win over the
// display name, so a host renamed at the client does not come back as a new
// record on the next upload.
export function buildIndex(existing, strategyKey) {
  const byKey = new Map();
  const byName = new Map();
  (existing || []).forEach((fs) => {
    const f = fieldsOf(fs);
    const recorded = f[IDENTITY_FIELD];
    if (recorded && f[IDENTITY_STRATEGY_FIELD] === strategyKey) {
      const k = normalizeKey(recorded);
      if (k && !byKey.has(k)) byKey.set(k, fs);
    }
    const n = normalizeKey(fs.name);
    if (n && !byName.has(n)) byName.set(n, fs);
  });
  return { byKey, byName };
}

// Would this strategy merge records that are NOT the same thing? Run before
// committing to a strategy, and show the result — this is the check that would
// have caught the 58 DNS/name collisions.
export function collisionReport(rows, strategyKey) {
  const get = strategyFor(strategyKey).get;
  const seen = new Map();
  const collisions = [];
  let blank = 0;
  (rows || []).forEach((row) => {
    const k = get(row);
    if (!k) { blank += 1; return; }
    if (seen.has(k)) {
      const first = seen.get(k);
      if (normalizeKey(first.name) !== normalizeKey(row.name)) {
        collisions.push({ key: k, a: first.name, b: row.name });
      }
    } else {
      seen.set(k, row);
    }
  });
  return { collisions, blank, distinct: seen.size, total: (rows || []).length };
}

// Decide, WITHOUT writing anything, what an import would do. The caller shows
// this and the user confirms — an import that silently creates 7,305 duplicates
// is exactly the failure this exists to prevent.
export function planImport(rows, existing, { strategy = 'name', allowNameFallback = true } = {}) {
  const get = strategyFor(strategy).get;
  const index = buildIndex(existing, strategy);
  const plan = { create: [], update: [], unchanged: [], skipped: [] };
  const usedThisRun = new Set();
  // existing fact-sheet id -> the row index that claimed it
  const claimedPriors = new Map();

  (rows || []).forEach((row, i) => {
    const key = get(row);
    const name = normalizeKey(row.name);
    if (!key && !name) { plan.skipped.push({ row, i, reason: 'no name and no identity key' }); return; }

    // Same key twice inside one file is a duplicate in the SOURCE, not a reason
    // to write the record twice.
    const dedupeKey = key || name;
    if (usedThisRun.has(dedupeKey)) {
      plan.skipped.push({ row, i, reason: 'duplicate of an earlier row in this same file' });
      return;
    }
    usedThisRun.add(dedupeKey);

    let prior = key ? index.byKey.get(key) : undefined;
    let matchedBy = prior ? 'identity key' : '';
    if (!prior && allowNameFallback && name) {
      prior = index.byName.get(name);
      if (prior) matchedBy = 'name';
    }

    if (!prior) { plan.create.push({ row, i, key }); return; }

    // `usedThisRun` above dedupes by SOURCE key, which is a different question
    // from whether two rows landed on the same existing record. Two rows with
    // different identity values but the same name both pass that check and then
    // both fall through to the name fallback onto one `prior`: the dry run
    // promised "2 will be UPDATED", the importer wrote the same fact sheet
    // twice, and the last row silently won. `collisionReport` cannot catch it
    // either — it only flags keys whose NAMES differ, and here they match.
    // A record can be claimed once per run; the second row is reported, not
    // written.
    const claimedBy = claimedPriors.get(prior.id);
    if (claimedBy !== undefined) {
      plan.skipped.push({
        row,
        i,
        reason: `row ${claimedBy + 2} of this file already matched the same record “${prior.name}” `
          + '— only the first row is applied, so nothing is silently overwritten',
      });
      return;
    }
    claimedPriors.set(prior.id, i);

    // Does this row actually change anything? An unchanged row should not
    // generate an audit entry claiming an edit that did not happen.
    const before = fieldsOf(prior);
    const changed = Object.keys(row.values || {}).filter((k) => {
      const a = row.values[k];
      const b = before[k];
      return String(a == null ? '' : a) !== String(b == null ? '' : b);
    });
    if (!changed.length) plan.unchanged.push({ row, i, prior, matchedBy });
    else plan.update.push({ row, i, prior, matchedBy, changed });
  });

  return plan;
}

// The values to stamp on every written record so the next import can match it.
export const identityStamp = (row, strategyKey, source) => ({
  [IDENTITY_FIELD]: strategyFor(strategyKey).get(row) || normalizeKey(row.name),
  [IDENTITY_STRATEGY_FIELD]: strategyKey,
  [SOURCE_FIELD]: source || '',
});

// ---------------------------------------------------------------------------
// Import run history — "have we loaded this exact file before?"
// ---------------------------------------------------------------------------
const runKey = (tenantId) => `primordia.imports.${tenantId}`;

// SHA-256 of the uploaded bytes. Same file re-uploaded => same fingerprint, so
// "v3 FINAL (1).xlsx" is recognised as the file already imported last week.
export async function fingerprint(arrayBuffer) {
  try {
    const buf = await crypto.subtle.digest('SHA-256', arrayBuffer);
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch (e) {
    return ''; // never block an import just because hashing was unavailable
  }
}

export function listRuns(tenantId) {
  try {
    return JSON.parse(localStorage.getItem(runKey(tenantId)) || '[]');
  } catch (e) {
    return [];
  }
}

export function findRunByFingerprint(tenantId, hash) {
  if (!hash) return null;
  return listRuns(tenantId).find((r) => r.fingerprint === hash) || null;
}

export function recordRun(tenantId, run) {
  try {
    const runs = listRuns(tenantId);
    runs.unshift({ ...run, at: new Date().toISOString() });
    localStorage.setItem(runKey(tenantId), JSON.stringify(runs.slice(0, 50)));
  } catch (e) { /* history is a convenience, never a reason to fail an import */ }
}
